import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';


const churnData = [
  { month: 'Jan', churnRate: 3.2, atRisk: 142, churned: 38 },
  { month: 'Feb', churnRate: 2.9, atRisk: 128, churned: 34 },
  { month: 'Mar', churnRate: 3.5, atRisk: 156, churned: 42 },
  { month: 'Apr', churnRate: 2.7, atRisk: 119, churned: 31 },
  { month: 'May', churnRate: 2.4, atRisk: 108, churned: 28 },
  { month: 'Jun', churnRate: 2.1, atRisk: 94, churned: 24 },
  { month: 'Jul', churnRate: 1.9, atRisk: 87, churned: 21 },
];

const signals = [
  { signal: 'No login in 14+ days', users: 312, severity: 'high', trend: '+12%' },
  { signal: 'Zero submissions this month', users: 248, severity: 'high', trend: '-5%' },
  { signal: 'Downgraded plan', users: 87, severity: 'medium', trend: '+3%' },
  { signal: 'Support tickets > 3', users: 64, severity: 'medium', trend: '+8%' },
  { signal: 'Low feature usage score', users: 193, severity: 'low', trend: '-2%' },
  { signal: 'Billing failure', users: 41, severity: 'high', trend: '+19%' },
];

const severityConfig = {
  high: { bg: 'bg-error/10', text: 'text-error', dot: 'bg-error', label: 'High' },
  medium: { bg: 'bg-warning/10', text: 'text-warning', dot: 'bg-warning', label: 'Medium' },
  low: { bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-muted-foreground', label: 'Low' },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        <p className="text-error">Churn Rate: {payload?.[0]?.value}%</p>
        <p className="text-warning">At Risk: {payload?.[1]?.value} users</p>
      </div>
    );
  }
  return null;
};

const ChurnSignals = () => {
  const [tab, setTab] = useState('signals');

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Churn Signals</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Early warning indicators for user churn</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {['signals', 'trend']?.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'signals' ? 'Signals' : 'Trend'}
            </button>
          ))}
        </div>
      </div>
      {tab === 'signals' ? (
        <div className="space-y-2">
          {signals?.map(s => {
            const cfg = severityConfig?.[s?.severity];
            const isPositive = s?.trend?.startsWith('-');
            return (
              <div key={s?.signal} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 hover:bg-muted/70 transition-colors">
                <div className={`w-2 h-2 rounded-full shrink-0 ${cfg?.dot}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{s?.signal}</p>
                </div>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${cfg?.bg} ${cfg?.text}`}>
                  {cfg?.label}
                </div>
                <span className="text-xs font-semibold text-foreground w-12 text-right shrink-0">
                  {s?.users?.toLocaleString()}
                </span>
                <span className={`text-xs font-medium w-10 text-right shrink-0 ${isPositive ? 'text-success' : 'text-error'}`}>
                  {s?.trend}
                </span>
              </div>
            );
          })}
          <div className="pt-3 border-t border-border flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Total at-risk users</span>
            <span className="text-sm font-bold text-error">945</span>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={churnData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} unit="%" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine yAxisId="left" y={3} stroke="var(--color-error)" strokeDasharray="4 2" label={{ value: 'Threshold', fontSize: 10, fill: 'var(--color-error)' }} />
            <Line yAxisId="left" type="monotone" dataKey="churnRate" stroke="var(--color-error)" strokeWidth={2.5} dot={{ r: 4, fill: 'var(--color-error)' }} name="Churn Rate" />
            <Line yAxisId="right" type="monotone" dataKey="atRisk" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="5 3" name="At Risk" />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ChurnSignals;
